// src/app/features/admin/store/admin.reducer.ts

import { createReducer, on } from '@ngrx/store';
import {
  AdminState, initialAdminState, AsyncSlice, CacheEntry,
  isCacheValid, emptySlice, ADMIN_TTL,
} from './admin.state';
import {
  AdminUsersActions, AdminBranchesActions,
  AdminUserDetailActions, AdminUserPatchActions,
} from './admin.actions';
import { UserListFilter } from '../models/admin.model';

// ── Helpers ─────────────────────────────────────────────────────────

function setLoading<T>(slice: AsyncSlice<T>): AsyncSlice<T> {
  return { ...slice, loading: true, error: null };
}

function setSuccess<T>(slice: AsyncSlice<T>, data: T, ttlMs: number): AsyncSlice<T> {
  const cache: CacheEntry<T> = { data, loadedAt: Date.now(), ttlMs };
  return { data, loading: false, loaded: true, error: null, cache };
}

function setFailure<T>(slice: AsyncSlice<T>, error: string): AsyncSlice<T> {
  return { ...slice, loading: false, error };
}

function filtersMatch(a: UserListFilter | null | undefined, b: UserListFilter | null | undefined): boolean {
  return JSON.stringify(a ?? {}) === JSON.stringify(b ?? {});
}

// ── Reducer ─────────────────────────────────────────────────────────

export const adminReducer = createReducer(
  initialAdminState,

  // ── Users ────────────────────────────────────────────────────────
  on(AdminUsersActions.load, (state, { filter, forceReload }): AdminState => {
    if (!forceReload && filtersMatch(filter, state.lastFilter) && isCacheValid(state.users.cache)) {
      return state;
    }
    return { ...state, users: setLoading(state.users) };
  }),
  on(AdminUsersActions.success, (state, { data, filter }): AdminState => ({
    ...state,
    users: setSuccess(state.users, data, ADMIN_TTL.USERS),
    lastFilter: filter ?? null,
  })),
  on(AdminUsersActions.failure, (state, { error }): AdminState => ({
    ...state, users: setFailure(state.users, error),
  })),
  on(AdminUsersActions.clear, (state): AdminState => ({
    ...state, users: emptySlice(), lastFilter: null,
  })),

  // ── Branches ─────────────────────────────────────────────────────
  on(AdminBranchesActions.load, (state, { forceReload }): AdminState => {
    if (!forceReload && isCacheValid(state.branches.cache)) return state;
    return { ...state, branches: setLoading(state.branches) };
  }),
  on(AdminBranchesActions.success, (state, { data }): AdminState => ({
    ...state, branches: setSuccess(state.branches, data, ADMIN_TTL.STATIC),
  })),
  on(AdminBranchesActions.failure, (state, { error }): AdminState => ({
    ...state, branches: setFailure(state.branches, error),
  })),

  // ── User Detail (keyed) ──────────────────────────────────────────
  on(AdminUserDetailActions.load, (state, { userId, forceReload }): AdminState => {
    const current = state.userDetail[userId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      userDetail: { ...state.userDetail, [userId]: setLoading(current) },
    };
  }),
  on(AdminUserDetailActions.success, (state, { userId, data }): AdminState => ({
    ...state,
    userDetail: {
      ...state.userDetail,
      [userId]: setSuccess(state.userDetail[userId] ?? emptySlice(), data, ADMIN_TTL.DETAIL),
    },
  })),
  on(AdminUserDetailActions.failure, (state, { userId, error }): AdminState => ({
    ...state,
    userDetail: {
      ...state.userDetail,
      [userId]: setFailure(state.userDetail[userId] ?? emptySlice(), error),
    },
  })),
  on(AdminUserDetailActions.clear, (state, { userId }): AdminState => {
    const { [userId]: _, ...rest } = state.userDetail;
    return { ...state, userDetail: rest };
  }),

  // ── Optimistic patches ───────────────────────────────────────────
  on(AdminUserPatchActions.patch2FA, (state, { userId, twoFactorEnabled, twoFactorMode, email_2FA, twoFactorConfirmed }): AdminState => {
    if (!state.users.data) return state;
    const data = state.users.data.map(u =>
      u.id === userId ? { ...u, twoFactorEnabled, twoFactorMode, email_2FA, twoFactorConfirmed } : u,
    );
    return { ...state, users: { ...state.users, data, error: null } };
  }),
  on(AdminUserPatchActions.patchActive, (state, { userId, active }): AdminState => {
    if (!state.users.data) return state;
    const data = state.users.data.map(u =>
      u.id === userId ? { ...u, active } : u,
    );
    return { ...state, users: { ...state.users, data, error: null } };
  }),
);
